import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronRight, MonitorSmartphone } from 'lucide-react'
import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { authApi, usersApi } from '@/api/endpoints'
import { keys } from '@/api/keys'
import type { User } from '@/api/types'
import { PageHeader } from '@/app/layouts/PageHeader'
import { Button } from '@/components/ui/Button'
import { useToast } from '@/components/ui/Toast'
import { Field, useCurrentUser, VerifyEmailCodeForm } from '@/features/auth'
import { SettingsSection as Section, TwoFactorSection } from '@/features/settings'
import { useT } from '@/i18n'
import { useFormat } from '@/lib/format'

export function SettingsAccountPage() {
  const t = useT()
  return (
    <>
      <PageHeader title={t('account.title')} back />
      <UsernameSection />
      <EmailSection />
      <PasswordSection />
      <TwoFactorSection />
      <SessionsSection />
      <Link
        to="/settings/deactivate"
        className="flex items-center gap-3 border-b border-line px-4 py-4 text-danger hover:bg-surface-hover"
      >
        <span className="flex-1 text-[15px] font-bold">{t('account.deactivate')}</span>
        <ChevronRight className="size-5" aria-hidden />
      </Link>
    </>
  )
}

/** 账号信息改完后，把 /auth/me 的缓存直接换成返回的新用户。 */
function useReplaceMe() {
  const client = useQueryClient()
  return (user: User) => client.setQueryData(keys.me(), user)
}

function UsernameSection() {
  const t = useT()
  const { user } = useCurrentUser()
  const replaceMe = useReplaceMe()
  const client = useQueryClient()
  const toast = useToast()
  const [value, setValue] = useState(user?.username ?? '')

  const change = useMutation({
    mutationFn: () => usersApi.changeUsername(value.trim()),
    onSuccess: (updated) => {
      if (user) client.removeQueries({ queryKey: keys.user(user.username) })
      replaceMe(updated)
      setValue(updated.username)
      toast.show(t('account.usernameChanged', { username: updated.username }))
    },
  })
  const error = change.error instanceof ApiError ? change.error : null

  if (!user) return null
  const unchanged = value.trim() === user.username

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!unchanged) change.mutate()
  }

  return (
    <Section
      title={t('account.usernameTitle')}
      description={
        <>
          {t('account.usernameDesc1')} <span className="font-mono">@{user.username}</span>{' '}
          {t('account.usernameDesc2')}
        </>
      }
    >
      <form onSubmit={onSubmit} className="max-w-sm space-y-3" noValidate>
        <Field
          label={t('account.usernameTitle')}
          name="username"
          autoComplete="username"
          value={value}
          maxLength={15}
          onChange={(e) => setValue(e.target.value)}
          error={error?.fieldError('username') ?? (error ? error.message : undefined)}
          required
        />
        <Button type="submit" loading={change.isPending} disabled={unchanged || !value.trim()}>
          {t('common.save')}
        </Button>
      </form>
    </Section>
  )
}

function EmailSection() {
  const t = useT()
  const { user } = useCurrentUser()
  const toast = useToast()
  const [editing, setEditing] = useState(false)
  const resend = useMutation({
    mutationFn: () => authApi.resendVerification(),
    onSuccess: (result) =>
      toast.show(result.sent ? t('account.verifySent') : t('verifyBanner.already')),
    onError: () => toast.error(t('verifyBanner.failed')),
  })

  if (!user) return null

  return (
    <Section title={t('account.emailTitle')}>
      <p className="text-[15px]">
        {user.email}{' '}
        {user.email_verified ? (
          <span className="ml-1 rounded bg-surface-subtle px-1.5 py-0.5 text-[13px] text-ink-muted">
            {t('account.verified')}
          </span>
        ) : (
          <span className="ml-1 rounded bg-danger/10 px-1.5 py-0.5 text-[13px] text-danger">
            {t('account.unverified')}
          </span>
        )}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {!user.email_verified && (
          <Button
            variant="outline"
            size="sm"
            loading={resend.isPending}
            onClick={() => resend.mutate()}
          >
            {t('account.resendVerify')}
          </Button>
        )}
        {!editing && (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            {t('account.changeEmail')}
          </Button>
        )}
      </div>
      {!user.email_verified && (
        <div className="mt-3">
          <VerifyEmailCodeForm />
        </div>
      )}
      {editing && <ChangeEmailForm onDone={() => setEditing(false)} />}
    </Section>
  )
}

function ChangeEmailForm({ onDone }: { onDone: () => void }) {
  const t = useT()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const replaceMe = useReplaceMe()
  const toast = useToast()

  const change = useMutation({
    mutationFn: () => authApi.changeEmail(email.trim(), password),
    onSuccess: (updated) => {
      replaceMe(updated)
      // email 在 User 上是可选的（只有看自己时才下发）。原来的模板字符串
      // 会把 undefined 直接拼成「已发到 undefined」，改成回退到刚提交的地址。
      toast.show(t('account.emailSentTo', { email: updated.email ?? email.trim() }))
      onDone()
    },
  })
  const error = change.error instanceof ApiError ? change.error : null
  const emailError = error?.fieldError('email')
  const passwordError = error?.code === 'wrong_password' ? error.message : undefined

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    change.mutate()
  }

  return (
    <form onSubmit={onSubmit} className="mt-4 max-w-sm space-y-3" noValidate>
      <p className="text-[15px] text-ink-muted">{t('account.emailChangeNote')}</p>
      <Field
        label={t('account.newEmail')}
        name="new_email"
        type="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={emailError}
        required
      />
      <Field
        label={t('account.currentPassword')}
        name="email_password"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={passwordError}
        required
      />
      {error && !emailError && !passwordError && (
        <p role="alert" className="text-sm text-danger">
          {error.message}
        </p>
      )}
      <div className="flex gap-2">
        <Button type="submit" loading={change.isPending} disabled={!email.trim() || !password}>
          {t('account.changeEmail')}
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}

function PasswordSection() {
  const t = useT()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const toast = useToast()
  const client = useQueryClient()

  const change = useMutation({
    mutationFn: () => authApi.changePassword(current, next),
    onSuccess: (result) => {
      setCurrent('')
      setNext('')
      client.invalidateQueries({ queryKey: keys.sessions() })
      toast.show(
        result.revoked_sessions > 1
          ? t('account.passwordUpdatedWithSessions', { count: result.revoked_sessions - 1 })
          : t('account.passwordUpdated'),
      )
    },
  })
  const error = change.error instanceof ApiError ? change.error : null

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    change.mutate()
  }

  return (
    <Section title={t('account.passwordTitle')}>
      <p className="mb-3 text-[15px] text-ink-muted">{t('account.passwordNote')}</p>
      <form onSubmit={onSubmit} className="max-w-sm space-y-3" noValidate>
        <Field
          label={t('account.currentPassword')}
          name="current_password"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          error={error?.fieldError('current_password')}
          required
        />
        <Field
          label={t('password.newLabel')}
          name="new_password"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          error={error?.fieldError('new_password')}
          required
        />
        {error && !error.fieldError('new_password') && !error.fieldError('current_password') && (
          <p role="alert" className="text-sm text-danger">
            {error.message}
          </p>
        )}
        <Button type="submit" loading={change.isPending}>
          {t('account.updatePassword')}
        </Button>
      </form>
    </Section>
  )
}

function SessionsSection() {
  const { relativeTime } = useFormat()
  const t = useT()
  const client = useQueryClient()
  const toast = useToast()
  const { data, isPending } = useQuery({
    queryKey: keys.sessions(),
    queryFn: () => authApi.sessions(),
  })

  const revoke = useMutation({
    mutationFn: (id: string) => authApi.revokeSession(id),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.sessions() })
      toast.show(t('account.signedOut'))
    },
    onError: () => toast.error(t('toast.actionFailed')),
  })

  return (
    <Section title={t('account.sessionsTitle')}>
      <p className="mb-3 text-[15px] text-ink-muted">{t('account.sessionsNote')}</p>
      {isPending && <p className="text-[15px] text-ink-muted">{t('common.loading')}</p>}
      {data?.length === 0 && (
        <p className="text-[15px] text-ink-muted">{t('account.noSessions')}</p>
      )}
      <ul className="space-y-3">
        {data?.map((session) => (
          <li key={session.id} className="flex items-start gap-3">
            <MonitorSmartphone className="mt-0.5 size-5 shrink-0 text-ink-muted" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[15px]">
                {describe(session.user_agent, t('account.unknownDevice'))}
              </p>
              <p className="text-[13px] text-ink-muted">
                {session.ip ?? t('account.unknownIp')} ·{' '}
                {t('account.loggedInAt', { time: relativeTime(session.created_at) })}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => revoke.mutate(session.id)}
              aria-label={t('account.signOutDevice', {
                device: describe(session.user_agent, t('account.unknownDevice')),
              })}
            >
              {t('account.signOut')}
            </Button>
          </li>
        ))}
      </ul>
    </Section>
  )
}

/**
 * User-Agent 太长，粗略认一下就够了 —— 这里只是帮用户回忆「这是哪台」。
 *
 * 认出来的都是平台与浏览器名（Windows · Chrome），本来就不翻译；
 * 只有认不出来时的兜底文案要跟着语言走，所以把它当参数传进来。
 */
function describe(userAgent: string | null, unknown: string): string {
  if (!userAgent) return unknown
  const platform = /Android|iPhone|iPad|Macintosh|Windows|Linux/.exec(userAgent)?.[0]
  const browser = /Firefox|Edg|Chrome|Safari/.exec(userAgent)?.[0]
  if (!platform && !browser) return userAgent.slice(0, 60)
  return [platform, browser === 'Edg' ? 'Edge' : browser].filter(Boolean).join(' · ')
}
